/**
 * Generic reference-switchboard view — header + "Go to Page" picker + grid.
 * Presentational/controlled: the per-function wrapper owns the state (pageKey,
 * search), the RTK query, and the column/width controllers, then passes them in.
 * This shares the full shell (header + picker + grid) across Inbound/Outbound
 * (and later Purchase/Sale) without conditional hooks.
 */
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material'
import { useMemo } from 'react'

import { COMBOBOX_FILTER_COMPACT, HIDE_ON_MOBILE } from '@/theme/responsive'
import type { UseDatasheetColumnsMenuResult } from '@/utils/datasheetColumns/types'

import ReferenceGridBase, { type ReferenceWidthController } from './ReferenceGridBase'
import type { ReferenceMenuColumn, ReferencePageConfig, TotalsMode } from './types'

export interface ReferencesViewProps<K extends string> {
  title: string
  subtitle: string
  pageOrder: K[]
  configs: Record<K, ReferencePageConfig<K>>
  pageKey: K
  onPageKeyChange: (key: K) => void
  // ── grid data (from wrapper's query) ──
  rows: Record<string, unknown>[]
  search: string
  onSearchChange: (value: string) => void
  loading?: boolean
  fetching?: boolean
  error?: unknown
  onRetry?: () => void
  // ── grid controllers (from wrapper's hooks) ──
  columnsCtrl: UseDatasheetColumnsMenuResult<ReferenceMenuColumn>
  widths: ReferenceWidthController
  totalsMode: TotalsMode
  fallbackToConfigWidth?: boolean
}

export default function ReferencesView<K extends string>({
  title,
  subtitle,
  pageOrder,
  configs,
  pageKey,
  onPageKeyChange,
  rows,
  search,
  onSearchChange,
  loading,
  fetching,
  error,
  onRetry,
  columnsCtrl,
  widths,
  totalsMode,
  fallbackToConfigWidth,
}: ReferencesViewProps<K>) {
  // Ép MỖI cột có `width` cụ thể (flex/minWidth → width) để grid dùng fixed
  // table-layout: cột wrap (description/note…) không phải tính lại intrinsic-width
  // theo mọi row khi scroll → hết giật lag. Cặp với `fallbackToConfigWidth` (mặc
  // định bật bên dưới) để width này có hiệu lực khi user chưa resize. Đồng bộ
  // hành vi 4 wrapper Inbound/Outbound/Purchase/Sale (Outbound vốn đã fixed-width).
  const rawPage = configs[pageKey]
  const page = useMemo(
    () => ({
      ...rawPage,
      columns: rawPage.columns.map((c) => ({ ...c, width: c.width ?? c.minWidth ?? 200 })),
    }),
    [rawPage],
  )
  const pageOptions = useMemo(
    () => pageOrder.map((key) => ({ value: key, label: configs[key].label })),
    [pageOrder, configs],
  )
  const selectedOption = pageOptions.find((o) => o.value === pageKey) ?? undefined

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, flex: 1 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 0.25 }}
      >
        <Box sx={HIDE_ON_MOBILE}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem', lineHeight: 1.4 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {subtitle}
          </Typography>
        </Box>
        <Stack alignItems="flex-end" spacing={0.5} sx={{ ml: 'auto' }}>
          {/* Skill /convert-form: combo → Autocomplete. PageKey là code internal
              nên dropdown chỉ display label. readOnly input → mobile không bung keyboard. */}
          <Autocomplete
            size="small"
            options={pageOptions}
            value={selectedOption}
            onChange={(_, v) => v && onPageKeyChange(v.value)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            disableClearable
            handleHomeEndKeys
            autoHighlight
            selectOnFocus
            sx={COMBOBOX_FILTER_COMPACT}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Go to Page:"
                InputLabelProps={{ shrink: true }}
                inputProps={{ ...params.inputProps, readOnly: true }}
              />
            )}
          />
        </Stack>
      </Stack>

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <ReferenceGridBase
          page={page}
          fillHeight
          rows={rows}
          search={search}
          onSearchChange={onSearchChange}
          loading={loading}
          fetching={fetching}
          error={error}
          onRetry={onRetry}
          columnsCtrl={columnsCtrl}
          widths={widths}
          totalsMode={totalsMode}
          fallbackToConfigWidth={fallbackToConfigWidth ?? true}
        />
      </Box>
    </Box>
  )
}
